import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Boxes } from "lucide-react";

const CATS = ["GENERAL", "TOILET", "KITCHEN", "EQUIPMENT"];
const FREQ = ["DAILY", "MONTHLY", "AS_REQUIRED", "MAINTAINED"];

export default function CatalogPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", category: "GENERAL", unit: "pcs", replenishment_frequency: "AS_REQUIRED", notes: "" });

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/catalog"); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function add(e) {
        e.preventDefault();
        try {
            await api.post("/catalog", { ...form, only_at_site_ids: [] });
            toast.success("Catalog item added");
            setForm({ name: "", category: "GENERAL", unit: "pcs", replenishment_frequency: "AS_REQUIRED", notes: "" });
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function updateField(id, patch) {
        try { await api.patch(`/catalog/${id}`, patch); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Consumables Catalogue</h1>
                <p className="text-sm text-gray-500 mt-1">Master items shared across all sites. Adding an item creates a disabled row at every site.</p>
            </div>
            <form onSubmit={add} className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 sm:p-5" data-testid="catalog-add-form">
                <div className="font-display font-semibold text-[#1E3355] mb-3">Add item</div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" required className={fieldCls + " sm:col-span-2"} data-testid="catalog-name" />
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={fieldCls} data-testid="catalog-category">{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                    <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Unit" required className={fieldCls} data-testid="catalog-unit" />
                    <select value={form.replenishment_frequency} onChange={(e) => setForm({ ...form, replenishment_frequency: e.target.value })} className={fieldCls} data-testid="catalog-freq">{FREQ.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                </div>
                <div className="mt-2"><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" className={fieldCls + " w-full"} data-testid="catalog-notes" /></div>
                <button type="submit" className="mt-3 inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="catalog-add-submit"><Plus className="w-4 h-4" /> Add to catalog</button>
            </form>
            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <table className="w-full text-sm" data-testid="catalog-table">
                    <thead className="bg-[#F4F6F9]">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Unit</th>
                            <th className="px-4 py-3">Frequency</th>
                            <th className="px-4 py-3">Notes</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? <tr><td colSpan="5" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr> :
                        rows.map((r) => (
                            <tr key={r.id} data-testid={`catalog-row-${r.name}`}>
                                <td className="px-4 py-3 font-medium text-[#1E3355] inline-flex items-center gap-2"><Boxes className="w-3.5 h-3.5 text-gray-400" /> {r.name}
                                    {r.only_at_site_ids?.length ? <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Site-restricted</span> : null}
                                </td>
                                <td className="px-4 py-3">
                                    <select defaultValue={r.category} onChange={(e) => updateField(r.id, { category: e.target.value })} className="text-xs rounded border border-gray-200 px-2 py-1">
                                        {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-600">{r.unit}</td>
                                <td className="px-4 py-3">
                                    <select defaultValue={r.replenishment_frequency} onChange={(e) => updateField(r.id, { replenishment_frequency: e.target.value })} className="text-xs rounded border border-gray-200 px-2 py-1">
                                        {FREQ.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">{r.notes || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const fieldCls = "rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
